import { useState } from "react";
import { communityPeople, communityPosts, emotionOptions } from "@/mocks/communication";
import CommunityPostCard from "@/pages/home/components/CommunityPostCard";
import CommunityDonationBanner from "@/pages/home/components/CommunityDonationBanner";
import PostCreateModal from "@/pages/home/components/PostCreateModal";
import DonationModal from "@/pages/home/components/DonationModal";
import CommunityAuthModal from "@/pages/home/components/CommunityAuthModal";
import { useCommunityAuth } from "@/hooks/useCommunityAuth";
import type { CommunityUser } from "@/hooks/useCommunityAuth";

const tagColorMap: Record<string, string> = {
  teal: "bg-teal-100 text-teal-700",
  rose: "bg-rose-100 text-rose-600",
  amber: "bg-amber-100 text-amber-700",
  green: "bg-green-100 text-green-700",
};

type ExtendedPost = typeof communityPosts[0] & { whatsapp?: string };

export default function CommunicationSection() {
  const { user, login, logout, isLoggedIn } = useCommunityAuth();
  const [activeTab, setActiveTab] = useState<"community" | "doctors" | "donate">("community");
  const [showPostModal, setShowPostModal] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [localPosts, setLocalPosts] = useState<ExtendedPost[]>(communityPosts);
  const [filterTag, setFilterTag] = useState("All");
  const [donateSuccess, setDonateSuccess] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);

  const tags = ["All", "Doctor", "Need Help", "Helper", "Donor", "Volunteer"];
  const filtered = filterTag === "All" ? localPosts : localPosts.filter((p) => p.tag === filterTag);

  const handleNewPost = (post: ExtendedPost) => {
    setLocalPosts((prev) => [post, ...prev]);
  };

  const handleDonate = () => {
    setShowDonateModal(false);
    setDonateSuccess(true);
    setTimeout(() => setDonateSuccess(false), 4000);
  };

  const handleAuthSuccess = (u: CommunityUser) => {
    login(u);
  };

  const handlePostClick = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    setShowPostModal(true);
  };

  const handleLoginRequired = () => {
    setShowAuthModal(true);
  };

  // Parse avatar string "AB|bg-teal-400"
  const getAvatarParts = (avatar: string) => {
    const parts = avatar.split("|");
    return { initials: parts[0] || "?", colorClass: parts[1] || "bg-stone-400" };
  };

  return (
    <section id="community" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="text-rose-500 text-sm font-semibold uppercase tracking-wider flex items-center justify-center gap-1.5 mb-1">
            <i className="ri-heart-fill"></i> People &amp; Community
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
            Emotion &amp; Community Communication Platform — Free for Everyone
          </h2>
          <p className="text-stone-500 text-sm mt-2 max-w-2xl mx-auto">
            Doctors, helpers, and people from all walks of life — connect freely, share what you need, offer help at no cost. And if you are blessed, welcome to make a small donation to help infants around the world who need love, milk, and warm clothes.
          </p>
        </div>

        {/* Auth bar */}
        <div className="flex items-center justify-between bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100 rounded-2xl px-5 py-3 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-pink-100 rounded-full">
              <i className="ri-community-line text-pink-500 text-base"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">
                {isLoggedIn ? `Welcome back, ${user!.name}!` : "Join Our Free Community"}
              </p>
              <p className="text-xs text-stone-500">
                {isLoggedIn
                  ? `Signed in as ${user!.contact} · ${user!.role}`
                  : "Sign up or log in to post, connect, and exchange WhatsApp"}
              </p>
            </div>
          </div>

          {isLoggedIn ? (
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu((v) => !v)}
                className="flex items-center gap-2 cursor-pointer"
              >
                {user!.avatar.includes("|") ? (
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full text-white text-xs font-bold ${getAvatarParts(user!.avatar).colorClass}`}>
                    {getAvatarParts(user!.avatar).initials}
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-full overflow-hidden">
                    <img src={user!.avatar} alt={user!.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <i className="ri-arrow-down-s-line text-stone-400 text-sm"></i>
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-12 bg-white border border-stone-100 rounded-xl w-52 z-30 overflow-hidden">
                  <div className="px-4 py-3 border-b border-stone-50">
                    <p className="font-bold text-gray-900 text-sm">{user!.name}</p>
                    <p className="text-xs text-stone-400">{user!.contact}</p>
                    {user!.whatsapp && (
                      <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                        <i className="ri-whatsapp-line"></i> {user!.whatsapp}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => { setShowPostModal(true); setShowProfileMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-edit-line text-teal-500"></i> Post to Community
                  </button>
                  <button
                    onClick={() => { logout(); setShowProfileMenu(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-rose-500 hover:bg-rose-50 flex items-center gap-2 cursor-pointer"
                  >
                    <i className="ri-logout-box-line"></i> Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-400 hover:to-rose-400 text-white font-bold rounded-lg text-xs transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line mr-1"></i> Sign Up Free
              </button>
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-4 py-2 bg-white border border-pink-200 text-pink-600 font-bold rounded-lg text-xs hover:bg-pink-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Sign In
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-stone-100 rounded-full p-1 gap-1">
            {[
              { key: "community", label: "Community Board", icon: "ri-community-line" },
              { key: "doctors", label: "Meet Our Helpers", icon: "ri-stethoscope-line" },
              { key: "donate", label: "Donate for Infants", icon: "ri-heart-fill" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab.key
                    ? tab.key === "donate"
                      ? "bg-rose-500 text-white"
                      : "bg-white text-gray-900 shadow-sm"
                    : "text-stone-500 hover:text-stone-800"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── COMMUNITY BOARD TAB ── */}
        {activeTab === "community" && (
          <div>
            {/* Emotion quick-post bar */}
            <div className="bg-stone-50 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 flex flex-wrap gap-2 justify-center sm:justify-start">
                {emotionOptions.map((e) => (
                  <button
                    key={e.key}
                    onClick={handlePostClick}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-stone-200 rounded-full text-xs font-semibold hover:border-rose-300 hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${e.icon} ${e.color}`}></i>
                    {e.label}
                  </button>
                ))}
              </div>
              <button
                onClick={handlePostClick}
                className="px-5 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
              >
                <i className="ri-edit-line"></i> Post What You Need
              </button>
            </div>

            {/* WhatsApp exchange info banner */}
            {!isLoggedIn && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-5 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-white text-sm"></i>
                </div>
                <p className="text-green-700 text-xs flex-1">
                  <strong>Exchange WhatsApp numbers</strong> with doctors, helpers, and community members.{" "}
                  <button onClick={() => setShowAuthModal(true)} className="underline font-bold cursor-pointer">
                    Sign up free
                  </button>{" "}
                  to unlock direct connections.
                </p>
              </div>
            )}

            {/* Filter tags */}
            <div className="flex flex-wrap gap-2 mb-5">
              {tags.map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterTag(t)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    filterTag === t
                      ? "bg-stone-900 text-white"
                      : "bg-white border border-stone-200 text-stone-600 hover:border-stone-400"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Posts */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-8">
              {filtered.map((post) => (
                <CommunityPostCard
                  key={post.id}
                  post={post}
                  onDonate={() => setShowDonateModal(true)}
                  isLoggedIn={isLoggedIn}
                  onLoginRequired={handleLoginRequired}
                />
              ))}
            </div>

            {/* Infant donation CTA inline */}
            <CommunityDonationBanner onDonate={() => setShowDonateModal(true)} />
          </div>
        )}

        {/* ── MEET OUR HELPERS TAB ── */}
        {activeTab === "doctors" && (
          <div>
            <div className="bg-gradient-to-r from-teal-50 to-rose-50 border border-teal-100 rounded-2xl p-5 mb-8 text-center">
              <i className="ri-stethoscope-line text-teal-500 text-3xl mb-2 block"></i>
              <h3 className="font-bold text-gray-900 text-lg mb-1">Doctors &amp; Helpers Are Here For You — For Free</h3>
              <p className="text-stone-600 text-sm max-w-xl mx-auto">
                Our community includes doctors, psychologists, nutritionists, social workers, and volunteers who offer their time freely. Reach out — no cost, no judgment.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
              {communityPeople.map((person) => (
                <div key={person.id} className="bg-white border border-stone-100 rounded-2xl p-5 hover:border-teal-200 transition-colors">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-full overflow-hidden">
                        <img src={person.avatar} alt={person.name} className="w-full h-full object-cover object-top" />
                      </div>
                      {person.online && (
                        <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-400 border-2 border-white rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-bold text-gray-900 text-sm">{person.name}</span>
                        <span className="text-base">{person.country}</span>
                      </div>
                      <p className="text-stone-500 text-xs">{person.role}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mt-1 inline-block ${tagColorMap[person.color] || "bg-stone-100 text-stone-600"}`}>
                        {person.tag}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs flex items-center gap-1 ${person.online ? "text-green-500" : "text-stone-400"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full inline-block ${person.online ? "bg-green-400" : "bg-stone-300"}`}></span>
                      {person.online ? "Online now" : "Offline"}
                    </span>
                    <div className="flex items-center gap-2">
                      {/* WhatsApp button */}
                      <button
                        onClick={isLoggedIn ? undefined : handleLoginRequired}
                        className="px-3 py-1.5 bg-green-500 hover:bg-green-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-whatsapp-line"></i>
                        <span className="hidden sm:inline">WhatsApp</span>
                      </button>
                      <button className="px-3 py-1.5 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-message-3-line mr-1"></i>Message
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Join as helper CTA */}
            <div className="bg-gradient-to-br from-stone-900 to-stone-800 rounded-2xl p-8 text-white text-center">
              <i className="ri-hand-heart-line text-4xl text-rose-400 mb-3 block"></i>
              <h3 className="text-xl font-bold mb-2">Are You a Doctor, Expert, or Volunteer?</h3>
              <p className="text-white/70 text-sm mb-5 max-w-lg mx-auto">
                Join our community of helpers. Offer free advice, consultations, or support. Together we can make a real difference — especially for infants and families in need.
              </p>
              <button
                onClick={handlePostClick}
                className="px-6 py-2.5 bg-teal-500 hover:bg-teal-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-1"></i> Join as a Helper
              </button>
            </div>
          </div>
        )}

        {/* ── DONATE TAB ── */}
        {activeTab === "donate" && (
          <div>
            {/* Hero */}
            <div className="relative rounded-2xl overflow-hidden mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/9a5dd9e6029da20786c1551cad4d4f48.jpg"
                alt="Infants who need care"
                className="w-full h-64 md:h-80 object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
              <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-12">
                <span className="inline-flex items-center gap-1.5 bg-rose-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3 w-fit">
                  <i className="ri-heart-fill"></i> Infant Charity
                </span>
                <h3 className="text-white text-2xl md:text-3xl font-bold leading-tight mb-2 max-w-lg">
                  Together We Can Give Every Infant a Chance
                </h3>
                <p className="text-white/80 text-sm mb-5 max-w-md">
                  Many infants around the world lack basic necessities — milk, warm clothes, medical care, and love. Your donation, however small, changes a life.
                </p>
                <button
                  onClick={() => setShowDonateModal(true)}
                  className="px-6 py-3 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2"
                >
                  <i className="ri-heart-fill"></i> Donate Now — Help an Infant
                </button>
              </div>
            </div>

            {/* Impact stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: "ri-heart-fill", color: "text-rose-500", bg: "bg-rose-50 border-rose-100", label: "Total Donated", value: "$184,320" },
                { icon: "ri-user-smile-line", color: "text-amber-500", bg: "bg-amber-50 border-amber-100", label: "Infants Helped", value: "3,210+" },
                { icon: "ri-global-line", color: "text-teal-500", bg: "bg-teal-50 border-teal-100", label: "Countries Reached", value: "47" },
                { icon: "ri-group-line", color: "text-rose-400", bg: "bg-rose-50 border-rose-100", label: "Donors This Month", value: "312" },
              ].map((stat) => (
                <div key={stat.label} className={`${stat.bg} border rounded-xl p-4 text-center`}>
                  <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2">
                    <i className={`${stat.icon} ${stat.color} text-xl`}></i>
                  </div>
                  <p className="font-bold text-gray-900 text-lg">{stat.value}</p>
                  <p className="text-stone-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* What your donation does */}
            <div className="bg-stone-50 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-gray-900 text-lg mb-5 text-center">What Your Donation Provides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: "ri-drop-line", color: "text-teal-500", bg: "bg-teal-50", amount: "$10", desc: "1 week of nutritious milk for one infant" },
                  { icon: "ri-shirt-line", color: "text-amber-500", bg: "bg-amber-50", amount: "$20", desc: "A full set of warm clothes for one baby" },
                  { icon: "ri-medicine-bottle-line", color: "text-rose-500", bg: "bg-rose-50", amount: "$50", desc: "One month of basic medical care" },
                  { icon: "ri-home-heart-line", color: "text-green-500", bg: "bg-green-50", amount: "$100", desc: "Safe shelter and care for one infant for a month" },
                ].map((item) => (
                  <div key={item.amount} className={`${item.bg} rounded-xl p-4 text-center`}>
                    <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                      <i className={`${item.icon} ${item.color} text-2xl`}></i>
                    </div>
                    <p className={`font-bold text-lg ${item.color} mb-1`}>{item.amount}</p>
                    <p className="text-stone-600 text-xs">{item.desc}</p>
                    <button
                      onClick={() => setShowDonateModal(true)}
                      className="mt-3 px-4 py-1.5 bg-rose-500 hover:bg-rose-400 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap w-full"
                    >
                      Donate {item.amount}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent donors */}
            <div className="bg-white border border-stone-100 rounded-2xl p-6 mb-8">
              <h3 className="font-bold text-gray-900 text-base mb-4 flex items-center gap-2">
                <i className="ri-heart-fill text-rose-400"></i> Recent Donors
              </h3>
              <div className="space-y-3">
                {[
                  { name: "Linda Z.", country: "🇨🇳", amount: "$100", time: "2 min ago", msg: "For all the little ones who need warmth." },
                  { name: "Michael T.", country: "🇺🇸", amount: "$50", time: "15 min ago", msg: "Happy to help. Every child deserves love." },
                  { name: "Fatima A.", country: "🇸🇦", amount: "$20", time: "1 hour ago", msg: "May God bless these infants." },
                  { name: "David L.", country: "🇦🇺", amount: "$200", time: "3 hours ago", msg: "Donating in honor of my newborn daughter." },
                  { name: "Yuki S.", country: "🇯🇵", amount: "$30", time: "5 hours ago", msg: "Small amount but from the heart." },
                ].map((donor, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-stone-50 last:border-0">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-rose-100 text-rose-500 font-bold text-xs flex-shrink-0">
                      {donor.name.slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-gray-900 text-sm">{donor.name}</span>
                        <span>{donor.country}</span>
                        <span className="text-xs text-stone-400">{donor.time}</span>
                      </div>
                      <p className="text-stone-500 text-xs italic truncate">&ldquo;{donor.msg}&rdquo;</p>
                    </div>
                    <span className="font-bold text-rose-500 text-sm flex-shrink-0">{donor.amount}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowDonateModal(true)}
                className="px-8 py-3.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-xl text-base transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-heart-fill"></i> Make a Donation for Infants
              </button>
              <p className="text-stone-400 text-xs mt-3">100% of donations go directly to infant care programs worldwide</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPostModal && (
        <PostCreateModal
          onClose={() => setShowPostModal(false)}
          onSubmit={handleNewPost}
          currentUser={user}
        />
      )}
      {showDonateModal && (
        <DonationModal
          onClose={() => setShowDonateModal(false)}
          onDonate={handleDonate}
        />
      )}
      {showAuthModal && (
        <CommunityAuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      {/* Success toast */}
      {donateSuccess && (
        <div className="fixed bottom-6 right-6 z-50 bg-rose-500 text-white px-5 py-3 rounded-xl flex items-center gap-3">
          <i className="ri-heart-fill text-lg"></i>
          <span className="font-semibold text-sm">Thank you! Your donation will change a life.</span>
        </div>
      )}
    </section>
  );
}
